export async function asyncForEach(array, callback) {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index, array)
  }
}

export function MapToArray(map: Map<string, number>) {
  return Array.from(map, ([name, value]) => ({ name, value }))
}

export function removeDuplicatesByProperty<T>(list: T[], property): T[] {
  const seen = new Set()
  return list.filter(item => {
    const value = item[property]
    if (!seen.has(value)) {
      seen.add(value)
      return true
    }
    return false
  })
}

/**
 * Returns a new array containing unique items.
 *
 * @param arr - The input array.
 * @returns A new array containing unique items.
 */
export const getUniqueArray = (arr: number[]): number[] => {
  return Array.from(new Set(arr))
}

/**
 * Returns a new array containing unique items based on a specified property.
 *
 * @param arr - The input array.
 * @param predicate - The property to determine uniqueness by, specified either as a string or a callback function.
 * @returns A new array containing unique items based on the specified property.
 */
type Predicate<T> = (item: T) => any

export const getUniqueArrayByProperty = <T>(arr: T[], predicate: keyof T | Predicate<T>): T[] => {
  const cb = typeof predicate === 'function' ? predicate : o => o[predicate]

  return [
    ...arr
      .reduce((map, item) => {
        const key = item === null || item === undefined ? item : cb(item)

        map.has(key) || map.set(key, item)

        return map
      }, new Map())
      .values()
  ]
}

/**
 * Checks if an array or object is empty.
 *
 * @param arr - The array or object to check.
 * @returns `true` if the array or object is empty, `false` otherwise.
 */
export const isEmpty = <T>(arr: T[] | object): boolean => {
  if (Array.isArray(arr)) {
    return arr.length === 0
  }

  if (typeof arr === 'object' && arr !== null) {
    return Object.keys(arr).length === 0
  }

  return arr === '' || arr === undefined || arr === null
}
